import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DateInput from '../forms/dateInput'
import { createClient, getClientsAsOptions, checkClientDuplicate } from '../../services/clients'
import { getUsersAsOptions } from '../../services/companyUsers'
import { createExpense } from '../../services/expenses'
import { createJob } from '../../services/jobs'
import { getServices } from '../../services/services'
import { rsdFormat, toTwoDecimals } from '../../utils/helper'

// Review card for Ask Soroby image scans. The AI extraction is a draft — the
// user corrects the fields here and nothing is saved until they confirm.

const TYPE_META = {
    expense: { icon: 'fa-receipt', titleKey: 'askSoroby.reviewExpense', titleFallback: 'Review expense', actionKey: 'askSoroby.saveExpense', actionFallback: 'Save expense' },
    client: { icon: 'fa-user-plus', titleKey: 'askSoroby.reviewClient', titleFallback: 'Review client', actionKey: 'askSoroby.addClient', actionFallback: 'Add client' },
    job: { icon: 'fa-briefcase', titleKey: 'askSoroby.reviewJob', titleFallback: 'Review job', actionKey: 'askSoroby.createJob', actionFallback: 'Create job' },
    unknown: { icon: 'fa-circle-question', titleKey: 'askSoroby.reviewScan', titleFallback: 'Review scan', actionKey: 'askSoroby.save', actionFallback: 'Save' },
}

const PICKUP_OPTIONS = [
    { value: 'PICKUP_FROM_HOME', labelKey: 'askSoroby.pickupFromHome', labelFallback: 'Pickup from home' },
    { value: 'CUSTOMER_DELIVERS', labelKey: 'askSoroby.customerDelivers', labelFallback: 'Customer delivers' },
]

const PAYMENT_METHODS = [
    { value: '', labelKey: 'askSoroby.notSet', labelFallback: 'Not set' },
    { value: 'CASH', labelKey: 'askSoroby.cash', labelFallback: 'Cash' },
    { value: 'CREDIT_CARD', labelKey: 'askSoroby.creditCard', labelFallback: 'Credit card' },
    { value: 'CHECK', labelKey: 'askSoroby.check', labelFallback: 'Check' },
]

// Defined at module level so typing in a field never remounts the input.
const Field = ({ label, wide, req, children }) => {
    const { t } = useTranslation()
    return (
        <label className={`as-x-field ${wide ? 'wide' : ''}`}>
            <span>{label}{req && <em className="as-x-req" title={t('common.required')}>*</em>}</span>
            {children}
        </label>
    )
}

const Section = ({ label, children }) => (
    <div className="as-x-section">
        <div className="as-x-section-label">{label}</div>
        {children}
    </div>
)

// ISO datetime -> value for <input type="datetime-local">, tolerant of nulls.
const toLocalInput = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d)) return ''
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const norm = (s) => String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'dj')
    .trim()

// Best-effort match of an extracted service name against the company's
// service list. Returns a serviceId or ''.
const matchService = (name, services) => {
    const q = norm(name)
    if (!q) return ''
    let best = { score: 0, id: '' }
    services.forEach((s) => {
        const n = norm(s.name)
        let score = 0
        if (n === q) score = 4
        else if (n.startsWith(q) || q.startsWith(n)) score = 3
        else if (n.includes(q) || q.includes(n)) score = 2
        else {
            const qTokens = q.split(/\s+/).filter(Boolean)
            const hit = qTokens.filter((t) => t.length > 2 && n.includes(t)).length
            if (hit && hit >= Math.ceil(qTokens.length / 2)) score = 1
        }
        if (score > best.score) best = { score, id: s.id }
    })
    return best.id
}

const num = (v) => {
    const n = parseFloat(v)
    return isNaN(n) ? 0 : n
}

// Recompute the tax chain for a line item. Prices typed by the user are kept
// as-is; only derived values (tax, total) are calculated.
const withTax = (item) => {
    const price = num(item.price)
    const taxAmount = toTwoDecimals((num(item.taxRate) / 100) * price)
    return { ...item, taxAmount, priceWithTax: toTwoDecimals(price + taxAmount) }
}

const draftItemsToRows = (draft, services) => {
    const raw = draft?.payload?.job?.lineItems
    const rows = (Array.isArray(raw) && raw.length ? raw : [{}]).map((li) => withTax({
        serviceId: matchService(li.serviceName, services),
        sourceName: li.serviceName || '',
        quantity: li.quantity ?? 1,
        price: li.price ?? li.priceWithTax ?? '',
        taxRate: li.taxRate ?? 0,
        // When the receipt shows no price we derive one from the matched
        // service's price-per-unit; a shown price is never overwritten.
        auto: li.price == null && li.priceWithTax == null,
    }))
    return rows.map((r) => {
        if (!r.auto || !r.serviceId) return r
        const svc = services.find((s) => s.id === r.serviceId)
        return svc ? withTax({ ...r, price: toTwoDecimals(num(svc.pricePerUnit) * num(r.quantity)) }) : r
    })
}

const ImageExtractCard = ({ draft, onDone, onDiscard }) => {
    const { t } = useTranslation()
    const type = TYPE_META[draft?.recordType] ? draft.recordType : 'unknown'
    const meta = TYPE_META[type]
    const payload = draft?.payload?.[type] || {}

    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')

    // --- Expense fields ---
    const [expense, setExpense] = useState(() => ({
        amount: payload.amount ?? '',
        date: toLocalInput(payload.date) || toLocalInput(new Date().toISOString()),
        paymentMethod: payload.paymentMethod || '',
        category: payload.category || '',
        description: payload.description || '',
        notes: payload.notes || '',
    }))

    // --- Client fields ---
    const [client, setClient] = useState(() => ({
        firstName: payload.firstName || '',
        lastName: payload.lastName || '',
        phone: payload.phone || '',
        email: payload.email || '',
        street: payload.street || '',
        apartmentNo: payload.apartmentNo || '',
        floor: payload.floor || '',
        intercomName: payload.intercomName || '',
        city: payload.city || '',
        state: payload.state || '',
        zipCode: payload.zipCode || '',
        country: payload.country || 'Serbia',
        notes: payload.notes || '',
    }))

    // Possible existing records for a scanned client — the scan flow's biggest
    // real-world risk is quietly creating duplicates of known clients.
    const [dupes, setDupes] = useState([])

    useEffect(() => {
        if (type !== 'client' || (!payload.phone && !payload.email && !(payload.firstName && payload.lastName))) return
        let alive = true
        checkClientDuplicate({
            phone: payload.phone || '',
            email: payload.email || '',
            firstName: payload.firstName || '',
            lastName: payload.lastName || '',
        }).then((res) => {
            if (alive && res?.code === 200 && Array.isArray(res.message)) setDupes(res.message)
        }).catch(() => {})
        return () => { alive = false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // --- Job fields ---
    const [services, setServices] = useState([])
    const [items, setItems] = useState([])
    const [job, setJob] = useState(() => ({
        pickupType: payload.pickupType === 'CUSTOMER_DELIVERS' ? 'CUSTOMER_DELIVERS' : 'PICKUP_FROM_HOME',
        schedTime: toLocalInput(payload.pickupTime || payload.deliveredByCustomerTime),
        paymentStatus: payload.paymentStatus === 'PAID' ? 'PAID' : 'PENDING',
        paymentMethod: ['CASH', 'CREDIT_CARD', 'CHECK'].includes(payload.paymentMethod) ? payload.paymentMethod : '',
        paymentType: ['BEFORE_SERVICE', 'DURING_SERVICE', 'AFTER_SERVICE'].includes(payload.paymentType) ? payload.paymentType : '',
        note: payload.note || '',
    }))

    const [assignees, setAssignees] = useState([])
    const [pickupAssignee, setPickupAssignee] = useState('')
    const [cleaningAssignee, setCleaningAssignee] = useState('')
    const [deliveryAssignee, setDeliveryAssignee] = useState('')

    // Client matcher: search-as-you-type over existing clients, seeded with the
    // name read from the image. A single hit is selected automatically.
    const [clientSel, setClientSel] = useState(null)
    const [clientQuery, setClientQuery] = useState(payload.clientName || '')
    const [clientOptions, setClientOptions] = useState([])
    const [searchingClients, setSearchingClients] = useState(false)
    const searchSeqRef = useRef(0)
    const autoPickedRef = useRef(false)

    useEffect(() => {
        if (type !== 'job') return
        let alive = true
        getServices().then((res) => {
            if (!alive || res?.code !== 200 || !Array.isArray(res.message)) return
            const active = res.message.filter((s) => !s.disabled)
            setServices(active)
            setItems(draftItemsToRows(draft, active))
        }).catch(() => { if (alive) setItems(draftItemsToRows(draft, [])) })
        getUsersAsOptions().then((res) => {
            if (!alive || res?.code !== 200 || !Array.isArray(res.message)) return
            setAssignees(res.message.map((u) => ({ value: u.id, label: u.fullName, roles: u.roles || '' })))
        }).catch(() => {})
        return () => { alive = false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (type !== 'job' || clientSel) return
        const q = clientQuery.trim()
        if (!q) { setClientOptions([]); setSearchingClients(false); return }
        // seq guards against a slow earlier response overwriting a newer one.
        const seq = ++searchSeqRef.current
        const t = setTimeout(async () => {
            setSearchingClients(true)
            try {
                const res = await getClientsAsOptions(q)
                if (seq !== searchSeqRef.current) return
                const opts = res?.code === 200 && Array.isArray(res.message) ? res.message : []
                setClientOptions(opts)
                // A single unambiguous match for the name read from the image is
                // picked automatically — but only once, so "Change" sticks.
                if (opts.length === 1 && !autoPickedRef.current && q === (payload.clientName || '').trim()) {
                    autoPickedRef.current = true
                    selectClient(opts[0])
                }
            } catch (e) {
                if (seq === searchSeqRef.current) setClientOptions([])
            } finally {
                if (seq === searchSeqRef.current) setSearchingClients(false)
            }
        }, 300)
        return () => clearTimeout(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientQuery, clientSel])

    const selectClient = (opt) => {
        setClientSel(opt)
        setClientOptions([])
        setError('')
    }

    const setItem = (index, patch) => {
        setItems((prev) => prev.map((item, i) => {
            if (i !== index) return item
            let next = { ...item, ...patch }
            if ('price' in patch) next.auto = false
            if ('serviceId' in patch || ('quantity' in patch && next.auto)) {
                const svc = services.find((s) => s.id === next.serviceId)
                // Fill the price from the service rate only while the user
                // hasn't typed one (auto rows) or the field is still empty.
                if (svc && (next.auto || num(next.price) === 0)) {
                    next.price = toTwoDecimals(num(svc.pricePerUnit) * num(next.quantity))
                    next.auto = true
                }
            }
            return withTax(next)
        }))
    }

    const addItem = () => setItems((prev) => [...prev, withTax({ serviceId: '', sourceName: '', quantity: 1, price: '', taxRate: 0, auto: true })])
    const removeItem = (index) => setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev))

    const total = useMemo(() => toTwoDecimals(items.reduce((acc, it) => acc + num(it.priceWithTax), 0)), [items])

    const fail = (res, fallback) => {
        throw new Error(typeof res?.message === 'string' && res.message ? res.message : fallback)
    }

    const save = async () => {
        if (saving) return
        setError('')

        try {
            if (type === 'expense') {
                if (num(expense.amount) <= 0) return setError(t('askSoroby.errorExpenseAmount', 'Enter the expense amount.'))
                if (!expense.category.trim()) return setError(t('askSoroby.errorExpenseCategory', 'Enter a category for this expense.'))
                setSaving(true)
                const res = await createExpense({
                    amount: num(expense.amount),
                    date: new Date(expense.date || Date.now()).toISOString(),
                    paymentMethod: expense.paymentMethod.trim(),
                    category: expense.category.trim(),
                    description: expense.description.trim() || draft?.summary || t('askSoroby.scannedReceipt', 'Scanned receipt'),
                    notes: expense.notes.trim(),
                })
                if (res?.code !== 200) fail(res, t('askSoroby.errorCouldNotSaveExpense', 'Could not save the expense.'))
                onDone(t('askSoroby.expenseSaved', 'Expense saved — {{amount}} · {{category}}.', { amount: rsdFormat(num(expense.amount)), category: expense.category.trim() }))
            } else if (type === 'client') {
                // Same required fields as the regular "New client" form, so
                // scanned clients are just as complete as hand-typed ones.
                const required = [
                    ['firstName', t('askSoroby.fieldFirstName', 'first name')],
                    ['lastName', t('askSoroby.fieldLastName', 'last name')],
                    ['phone', t('askSoroby.fieldPhone', 'phone number')],
                    ['street', t('askSoroby.fieldStreet', 'street')],
                    ['city', t('askSoroby.fieldCity', 'city')],
                    ['zipCode', t('askSoroby.fieldZipCode', 'zip code')],
                    ['state', t('askSoroby.fieldState', 'state')],
                    ['country', t('askSoroby.fieldCountry', 'country')],
                ]
                const missing = required.find(([key]) => !client[key].trim())
                if (missing) return setError(t('askSoroby.errorClientField', "Enter the client's {{field}}.", { field: missing[1] }))
                setSaving(true)
                const res = await createClient({
                    ...client,
                    firstName: client.firstName.trim(),
                    lastName: client.lastName.trim(),
                    phone: client.phone.trim(),
                    email: client.email.trim(),
                    zipCode: client.zipCode.slice(0, 10),
                })
                if (res?.code !== 200) fail(res, t('askSoroby.errorCouldNotAddClient', 'Could not add the client.'))
                onDone(t('askSoroby.clientAdded', 'Client added: {{name}}.', { name: `${client.firstName} ${client.lastName}`.trim() }))
            } else if (type === 'job') {
                const lines = items.filter((it) => it.serviceId)
                if (!clientSel) return setError(t('askSoroby.errorChooseClient', 'Choose a client for this job.'))
                if (!job.schedTime) return setError(job.pickupType === 'PICKUP_FROM_HOME' ? t('askSoroby.errorSetPickupTime', 'Set the pickup time.') : t('askSoroby.errorSetDropOffTime', 'Set the drop-off time.'))
                if (!lines.length) return setError(t('askSoroby.errorPickService', 'Pick a service for at least one line.'))
                if (lines.some((it) => num(it.quantity) <= 0)) return setError(t('askSoroby.errorQuantityAboveZero', 'Every service line needs a quantity above zero.'))
                if (items.some((it) => !it.serviceId && num(it.price) > 0)) return setError(t('askSoroby.errorLineNoService', 'A line with a price has no service picked — choose a service for it or remove the line.'))
                if (job.paymentStatus === 'PAID' && !job.paymentMethod) return setError(t('askSoroby.errorChoosePaymentMethod', 'Choose how the job was paid.'))
                if (job.paymentStatus === 'PAID' && !job.paymentType) return setError(t('askSoroby.errorChoosePaymentType', 'Choose when the job was paid.'))
                setSaving(true)
                const schedIso = new Date(job.schedTime).toISOString()
                // Job price must equal what the saved line items add up to.
                const linesTotal = toTwoDecimals(lines.reduce((acc, it) => acc + num(it.priceWithTax), 0))
                const res = await createJob({
                    clientId: clientSel.id,
                    pickupType: job.pickupType,
                    pickupTime: job.pickupType === 'PICKUP_FROM_HOME' ? schedIso : null,
                    deliveredByCustomerTime: job.pickupType === 'CUSTOMER_DELIVERS' ? schedIso : null,
                    status: 'SCHEDULED',
                    price: num(linesTotal),
                    paymentStatus: job.paymentStatus,
                    paymentMethod: job.paymentStatus === 'PAID' ? job.paymentMethod : (job.paymentMethod || null),
                    paymentType: job.paymentStatus === 'PAID' ? job.paymentType : null,
                    paymentTime: job.paymentStatus === 'PAID' ? new Date().toISOString() : null,
                    pickupAssignedToId: pickupAssignee || null,
                    cleaningAssignedToId: cleaningAssignee || null,
                    deliveryAssignedToId: deliveryAssignee || null,
                    note: job.note.trim() || null,
                    lineItems: lines.map((it) => ({
                        quantity: num(it.quantity),
                        price: num(it.price),
                        taxRate: num(it.taxRate),
                        taxAmount: num(it.taxAmount),
                        priceWithTax: num(it.priceWithTax),
                        serviceId: it.serviceId,
                        badgeId: null,
                    })),
                })
                if (res?.code !== 200) fail(res, t('askSoroby.errorCouldNotCreateJob', 'Could not create the job.'))
                onDone(t('askSoroby.jobCreated', 'Job created for {{name}} — {{amount}}.', { name: clientSel.name, amount: rsdFormat(num(linesTotal)) }))
            } else {
                setError(t('askSoroby.errorUnknownRecord', "I couldn't tell what kind of record this is. Try a clearer photo of a receipt, business card or order slip."))
            }
        } catch (err) {
            setError(err?.message || t('askSoroby.errorSomethingWentWrong', 'Something went wrong while saving. Please try again.'))
        } finally {
            setSaving(false)
        }
    }

    const lowConfidence = typeof draft?.confidence === 'number' && draft.confidence < 0.75

    return (
        <div className="as-x-card">
            <div className="as-x-head">
                <div className="as-x-head-icon"><i className={`fa-solid ${meta.icon}`}></i></div>
                <div className="as-x-head-text">
                    <div className="as-x-kicker">{t('askSoroby.imageScan', 'Image scan')}</div>
                    <div className="as-x-title">{t(meta.titleKey, meta.titleFallback)}</div>
                </div>
                {draft?.imageUrl && (
                    <a className="as-x-thumb" href={draft.imageUrl} target="_blank" rel="noreferrer" title={t('askSoroby.openUploadedImage', 'Open the uploaded image')}>
                        <img src={draft.imageUrl} alt={t('askSoroby.uploadedScan', 'Uploaded scan')} />
                    </a>
                )}
            </div>

            {(lowConfidence || draft?.warnings?.length > 0) && (
                <div className="as-x-notes">
                    {lowConfidence && <div><i className="fa-solid fa-triangle-exclamation"></i> {t('askSoroby.lowConfidenceWarning', "I'm not fully sure I read this correctly — please double-check the fields.")}</div>}
                    {(draft?.warnings || []).map((w, i) => <div key={i}><i className="fa-solid fa-circle-info"></i> {w}</div>)}
                </div>
            )}

            {type === 'expense' && (
                <div className="as-x-grid">
                    <Field label={t('askSoroby.amountRsd', 'Amount (RSD)')}><input type="number" step="0.01" min="0" inputMode="decimal" value={expense.amount} onChange={(e) => setExpense({ ...expense, amount: e.target.value })} /></Field>
                    <Field label={t('common.date', 'Date')}><DateInput type="datetime-local" value={expense.date} onChange={(e) => setExpense({ ...expense, date: e.target.value })} /></Field>
                    <Field label={t('common.category', 'Category')}><input value={expense.category} onChange={(e) => setExpense({ ...expense, category: e.target.value })} placeholder={t('askSoroby.egSupplies', 'e.g. Supplies')} /></Field>
                    <Field label={t('askSoroby.paymentMethod', 'Payment method')}><input value={expense.paymentMethod} onChange={(e) => setExpense({ ...expense, paymentMethod: e.target.value })} placeholder={t('askSoroby.egCash', 'e.g. Cash')} /></Field>
                    <Field label={t('common.description', 'Description')} wide><input value={expense.description} onChange={(e) => setExpense({ ...expense, description: e.target.value })} placeholder={t('askSoroby.merchantOrReason', 'Merchant or reason')} /></Field>
                    <Field label={t('common.notes', 'Notes')} wide><textarea rows={2} value={expense.notes} onChange={(e) => setExpense({ ...expense, notes: e.target.value })} /></Field>
                </div>
            )}

            {type === 'client' && (
                <>
                    {dupes.length > 0 && (
                        <div className="as-x-dupes">
                            <div className="as-x-dupes-title"><i className="fa-solid fa-user-group"></i> {t('askSoroby.clientMayExist', 'This client may already exist')}</div>
                            {dupes.map((d) => (
                                <div key={d.id} className="as-x-dupes-row">
                                    <strong>{d.name}</strong>
                                    {d.phone && <span>{d.phone}</span>}
                                    <em>{d.matchedBy === 'phone' ? t('askSoroby.samePhone', 'same phone') : t('askSoroby.sameName', 'same name')}</em>
                                </div>
                            ))}
                            <div className="as-x-dupes-note">{t('askSoroby.dupeWarning', 'Saving will create a second record — discard if this is the same person.')}</div>
                        </div>
                    )}
                    <div className="as-x-grid">
                        <Field label={t('askSoroby.firstName', 'First name')} req><input value={client.firstName} onChange={(e) => setClient({ ...client, firstName: e.target.value })} /></Field>
                        <Field label={t('askSoroby.lastName', 'Last name')} req><input value={client.lastName} onChange={(e) => setClient({ ...client, lastName: e.target.value })} /></Field>
                        <Field label={t('askSoroby.phone', 'Phone')} req><input type="tel" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} /></Field>
                        <Field label={t('askSoroby.email', 'Email')}><input type="email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} /></Field>
                        <Field label={t('askSoroby.street', 'Street')} req wide><input value={client.street} onChange={(e) => setClient({ ...client, street: e.target.value })} /></Field>
                        <Field label={t('askSoroby.apartment', 'Apartment')}><input value={client.apartmentNo} onChange={(e) => setClient({ ...client, apartmentNo: e.target.value })} /></Field>
                        <Field label={t('askSoroby.floor', 'Floor')}><input value={client.floor} onChange={(e) => setClient({ ...client, floor: e.target.value })} /></Field>
                        <Field label={t('askSoroby.intercom', 'Intercom')}><input value={client.intercomName} onChange={(e) => setClient({ ...client, intercomName: e.target.value })} /></Field>
                        <Field label={t('askSoroby.city', 'City')} req><input value={client.city} onChange={(e) => setClient({ ...client, city: e.target.value })} /></Field>
                        <Field label={t('askSoroby.zipCode', 'Zip code')} req><input maxLength={10} value={client.zipCode} onChange={(e) => setClient({ ...client, zipCode: e.target.value })} /></Field>
                        <Field label={t('askSoroby.state', 'State')} req><input value={client.state} onChange={(e) => setClient({ ...client, state: e.target.value })} /></Field>
                        <Field label={t('askSoroby.country', 'Country')} req><input value={client.country} onChange={(e) => setClient({ ...client, country: e.target.value })} /></Field>
                        <Field label={t('common.notes', 'Notes')} wide><textarea rows={2} value={client.notes} onChange={(e) => setClient({ ...client, notes: e.target.value })} placeholder={t('askSoroby.accessNotesPlaceholder', 'Access notes, preferences, anything the driver should know…')} /></Field>
                    </div>
                </>
            )}

            {type === 'job' && (
                <>
                    <Section label={t('askSoroby.clientSection', 'Client')}>
                        {clientSel ? (
                            <div className="as-x-clientpick">
                                <i className="fa-solid fa-user"></i>
                                <div className="as-x-clientpick-text">
                                    <strong>{clientSel.name}</strong>
                                    {clientSel.phone && <span>{clientSel.phone}</span>}
                                </div>
                                <button type="button" onClick={() => { setClientSel(null); setClientOptions([]) }}>{t('askSoroby.change', 'Change')}</button>
                            </div>
                        ) : (
                            <div className="as-x-clientsearch">
                                <div className="as-x-clientsearch-box">
                                    <i className={`fa-solid ${searchingClients ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'}`}></i>
                                    <input
                                        value={clientQuery}
                                        onChange={(e) => setClientQuery(e.target.value)}
                                        placeholder={t('askSoroby.searchClients', 'Search clients by name or phone')}
                                    />
                                </div>
                                {clientOptions.length > 0 && (
                                    <div className="as-x-clientsearch-list">
                                        {clientOptions.map((o) => (
                                            <button key={o.id} type="button" onClick={() => selectClient(o)}>
                                                <strong>{o.name}</strong>
                                                {o.phone && <span>{o.phone}</span>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {!searchingClients && clientQuery.trim() && clientOptions.length === 0 && (
                                    <div className="as-x-clientsearch-empty">{t('askSoroby.noMatchingClient', 'No matching client — try another name, or add them first from a business-card scan.')}</div>
                                )}
                            </div>
                        )}
                    </Section>

                    <Section label={t('askSoroby.schedule', 'Schedule')}>
                        <div className="as-x-grid tight">
                            <Field label={t('common.type', 'Type')}>
                                <select value={job.pickupType} onChange={(e) => setJob({ ...job, pickupType: e.target.value })}>
                                    {PICKUP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.labelFallback)}</option>)}
                                </select>
                            </Field>
                            <Field label={job.pickupType === 'PICKUP_FROM_HOME' ? t('askSoroby.pickupTime', 'Pickup time') : t('askSoroby.dropOffTime', 'Drop-off time')}>
                                <DateInput type="datetime-local" value={job.schedTime} onChange={(e) => setJob({ ...job, schedTime: e.target.value })} />
                            </Field>
                        </div>
                    </Section>

                    <Section label={t('askSoroby.assignees', 'Assignees')}>
                        <div className="as-x-grid tight">
                            <Field label={t('askSoroby.pickup', 'Pickup')}>
                                <select value={pickupAssignee} onChange={(e) => setPickupAssignee(e.target.value)}>
                                    <option value="">{t('askSoroby.notAssigned', 'Not assigned')}</option>
                                    {assignees.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                                </select>
                            </Field>
                            <Field label={t('askSoroby.service', 'Service')}>
                                <select value={cleaningAssignee} onChange={(e) => setCleaningAssignee(e.target.value)}>
                                    <option value="">{t('askSoroby.notAssigned', 'Not assigned')}</option>
                                    {assignees.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                                </select>
                            </Field>
                            <Field label={t('askSoroby.delivery', 'Delivery')}>
                                <select value={deliveryAssignee} onChange={(e) => setDeliveryAssignee(e.target.value)}>
                                    <option value="">{t('askSoroby.notAssigned', 'Not assigned')}</option>
                                    {assignees.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                                </select>
                            </Field>
                        </div>
                    </Section>

                    <Section label={t('askSoroby.services', 'Services')}>
                        {items.map((it, i) => (
                            <div key={i} className="as-x-item">
                                <div className="as-x-item-row">
                                    <select value={it.serviceId} onChange={(e) => setItem(i, { serviceId: e.target.value })}>
                                        <option value="">{t('askSoroby.chooseService', 'Choose a service…')}</option>
                                        {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                    <input className="qty" type="number" min="0" step="any" inputMode="decimal" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} aria-label={t('common.quantity', 'Quantity')} title={t('common.quantity', 'Quantity')} />
                                    <input className="price" type="number" min="0" step="0.01" inputMode="decimal" value={it.price} onChange={(e) => setItem(i, { price: e.target.value })} aria-label={t('askSoroby.priceRsd', 'Price (RSD)')} title={t('askSoroby.priceRsd', 'Price (RSD)')} />
                                    <button type="button" className="as-x-item-del" onClick={() => removeItem(i)} disabled={items.length === 1} aria-label={t('askSoroby.removeLine', 'Remove line')} title={t('askSoroby.removeLine', 'Remove line')}>
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                </div>
                                {it.sourceName && !it.serviceId && (
                                    <div className="as-x-item-hint">{t('askSoroby.fromImage', 'From the image: "{{name}}"', { name: it.sourceName })}</div>
                                )}
                            </div>
                        ))}
                        <div className="as-x-item-foot">
                            <button type="button" className="as-x-additem" onClick={addItem}><i className="fa-solid fa-plus"></i> {t('askSoroby.addService', 'Add service')}</button>
                            <div className="as-x-total">{t('common.total', 'Total')} <strong>{rsdFormat(num(total))}</strong></div>
                        </div>
                    </Section>

                    <Section label={t('askSoroby.payment', 'Payment')}>
                        <div className="as-x-grid tight">
                            <Field label={t('common.status', 'Status')}>
                                <select value={job.paymentStatus} onChange={(e) => setJob({ ...job, paymentStatus: e.target.value })}>
                                    <option value="PENDING">{t('common.pending', 'Pending')}</option>
                                    <option value="PAID">{t('common.paid', 'Paid')}</option>
                                </select>
                            </Field>
                            <Field label={t('askSoroby.method', 'Method')}>
                                <select value={job.paymentMethod} onChange={(e) => setJob({ ...job, paymentMethod: e.target.value })}>
                                    {PAYMENT_METHODS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.labelFallback)}</option>)}
                                </select>
                            </Field>
                            {job.paymentStatus === 'PAID' && (
                                <Field label={t('askSoroby.paidWhen', 'Paid when')} wide>
                                    <select value={job.paymentType} onChange={(e) => setJob({ ...job, paymentType: e.target.value })}>
                                        <option value="">{t('askSoroby.choose', 'Choose…')}</option>
                                        <option value="BEFORE_SERVICE">{t('askSoroby.beforeTheService', 'Before the service')}</option>
                                        <option value="DURING_SERVICE">{t('askSoroby.duringTheService', 'During the service')}</option>
                                        <option value="AFTER_SERVICE">{t('askSoroby.afterTheService', 'After the service')}</option>
                                    </select>
                                </Field>
                            )}
                            <Field label={t('askSoroby.note', 'Note')} wide>
                                <textarea rows={2} value={job.note} onChange={(e) => setJob({ ...job, note: e.target.value })} />
                            </Field>
                        </div>
                    </Section>
                </>
            )}

            {type === 'unknown' && (
                <div className="as-x-unknown">
                    {draft?.rawText
                        ? <>{t('askSoroby.readFromImageButCouldntMap', "I read this from the image but couldn't map it to a record:")}<pre>{draft.rawText}</pre></>
                        : t('askSoroby.couldntReadImage', "I couldn't read a receipt, business card or order slip in this image. Try a sharper, well-lit photo.")}
                </div>
            )}

            {error && <div className="as-x-error"><i className="fa-solid fa-circle-exclamation"></i> {error}</div>}

            <div className="as-x-actions">
                <button type="button" className="secondary" onClick={onDiscard} disabled={saving}>{t('common.discard', 'Discard')}</button>
                {type !== 'unknown' && (
                    <button type="button" className="primary" onClick={save} disabled={saving}>
                        {saving ? <><i className="fa-solid fa-spinner fa-spin"></i> {t('askSoroby.saving', 'Saving…')}</> : t(meta.actionKey, meta.actionFallback)}
                    </button>
                )}
            </div>
        </div>
    )
}

export default ImageExtractCard
